import { game } from "../gameManager";
import { Ingredient } from "../data/ingredientModel";
import { RecipePage } from "../recipes/recipePage";
import { createDiv, createElem, delay, getOneByClass, padNum } from "../util";
import { Reel, Symbol } from "./reel";
import { Recipe } from "../data/recipeModel";
import { sounds } from "../soundManager";

export enum CauldronState {
    empty,
    brewing,
    spinning,
    complete,
    failed
}
const StateNames = ["empty", "brewing", "spinning", "complete", "failed"];

export class Cauldron {
    div: HTMLElement;
    recipePage: RecipePage;
    recipe: Recipe = null;
    ingredients: Ingredient[] = [];
    reels: Reel[] = [
        new Reel(),
        new Reel(),
        new Reel()
    ];
    state = CauldronState.empty;

    createElement(parent: HTMLElement) {
        // We don't really need to generate this, we can just grab something from the HTML,
        // but for ease since there are 3..
        
        this.div = createDiv(parent, "cauldron empty");

        createDiv(this.div, "timer", "00:00");

        const reelsDiv = createDiv(this.div, "reels");
        for (var reel of this.reels){
            reel.createElement(reelsDiv);
        }

        const results = createDiv(this.div, "results");
        createDiv(results, "stars inline-stars");
        createDiv(results, "price");
        createElem("img", results, "potion");

        const display = createDiv(this.div, "display");
        createDiv(display, "contents");
        createDiv(display, "fire");
        const img = createElem("img", display, "cauldron-img") as HTMLImageElement;
        img.src = "assets/cauldron.png";
        
        this.reset();
    }

    setState(state: CauldronState) {
        this.state = state;
        this.div.className = "cauldron " + StateNames[state];

        getOneByClass("fire", this.div).style.backgroundColor =
            state === CauldronState.brewing || state === CauldronState.spinning ? "#ffa62c" : "#675370";
        getOneByClass("contents", this.div).style.backgroundColor = this.getCauldronColor();
    
        game.determineWitchExpression(state);
    }

    async bounce() {
        this.div.style.transform = "scale(1.02)";
        await delay(200);
        this.div.style.transform = "scale(1)";
    }

    brew(recipePage: RecipePage) {
        this.recipePage = recipePage;
        this.recipe = recipePage.recipe;
        this.setState(CauldronState.brewing);

        for (let slot of recipePage.slots) {
            this.ingredients.push(slot.ingredient);
            for (let i=0; i<3; i++) {
                this.reels[i].addOption(slot.ingredient.symbols[i]);
            }
        }

        this.setTimer(this.recipe.time);
        this.bounce();

        sounds.play("drop2");
        //sounds.play("bubbling");
    }

    setTimer(timeInSeconds: number) {
        var timer = getOneByClass("timer", this.div);

        var endTime = new Date().getTime() + timeInSeconds * 1000;
        this.displayRemainingTime(endTime);
        // TODO this seems to jump from 20 to 18... debug later 

        var interval = setInterval(() => {
            this.displayRemainingTime(endTime, async () => {
                clearInterval(interval);
                timer.innerHTML = "00:00";
                
                await delay(1000); // show zero before spinning
                this.spin();
            });
        }, 1000);
    }

    displayRemainingTime(endTime: number, onComplete?) {
        var timer = getOneByClass("timer", this.div);
        var now = new Date().getTime();
        var distance = endTime - now;

        if (distance > 0) {
            var minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)); // what remains after accounting for hours
            var seconds = Math.ceil((distance % (1000 * 60)) / 1000); // what remains after accounting for minutes
        
            timer.innerHTML = `${padNum(minutes, 2)}:${padNum(seconds, 2)}`;
        }
        else {
            onComplete();
        }
    }

    async spin() {
        this.setState(CauldronState.spinning);
        sounds.play("ding");

        // Stop bubbling if no other cauldrons are still bubbling (put this elsewhere?)
        if (!game.cauldrons.find(c => c.state === CauldronState.brewing)) {
            sounds.stop("bubbling");
        }

        const spinTime = 1000; // time reel spins for
        const spinSpeed = 200; // time for one symbol to cross
        const promises = this.reels.map((r, i) => r.spin(500 + (spinTime * (i+1)), spinSpeed));
        await Promise.all(promises);

        await delay(500);
        this.getResult();
    }

    getResult() {
        const xs = this.reels.filter(r => r.currentSymbol === Symbol.X).length;
        if (xs === 3) {
            this.setState(CauldronState.failed);

            const potion = getOneByClass("potion", this.div) as HTMLImageElement;
            potion.src = `assets/icons/x_icon.png`;

            this.div.onclick = () => {
                this.reset();
                sounds.play("whoosh");
            }

            sounds.play("boom");
            sounds.play("bad");
        }
        else {
            this.setState(CauldronState.complete);

            const stars = this.reels.filter(r => r.currentSymbol === Symbol.Star).length;
            const price = this.recipe.price[stars];

            getOneByClass("stars", this.div).innerHTML = '<div class="symbol symbol-1"></div>'.repeat(stars);
            getOneByClass("price", this.div).innerHTML = `<span class="money">${price.toString()}</span><span class="coin"></span>`;

            const potion = getOneByClass("potion", this.div) as HTMLImageElement;
            potion.src = `assets/potions/${this.recipe.icon}.png`;
            
            this.div.onclick = () => this.sellPotion(price, stars);

            sounds.play("pour");
            sounds.play(["star1", "star2", "star3", "win"][stars]);
        }

        this.bounce();
    }

    sellPotion(price: number, stars: number) {
        game.addMoney(price);
        this.recipePage.onResults(stars);
        this.reset();
    }

    reset() {
        this.setState(CauldronState.empty);

        this.recipe = null;
        this.ingredients = [];
        this.reels.forEach(r => r.reset());
        this.div.onclick = null;
    }

    getCauldronColor() {
        switch (this.state) {
            case CauldronState.empty:
            case CauldronState.complete:
                return "#351e3e";
            case CauldronState.failed:
                return "#000000";
            default:
                switch (this.recipe?.name) {
                    case "Slime Potion":
                        return "#2ab056";
                    case "Potion of Lesser Healing":
                        return "#ff7888";
                    case "Potion of Greater Healing":
                        return "#ed4e61";
                    case "Giant Strength Potion":
                        return "#ffb31e";
                    case "Cat Potion":
                        return "#a074e4";
                    case "Love Potion":
                        return "#ff5eb7";
                    case "Fondue":
                        return "#ffe7b8";
                    case "Potion of Invisilibity":
                        return "#d4e1f0";
                    case "Potion of Game Development":
                        return "#ff8b00";
                    case "Magnificent Potion":
                        return "#6799ff"; // TODO rainbow potion...
                    default:
                        return "#ffffff";
                }
        }
    }
}